// WYSIWYG contenteditable for footnote / endnote body editing.
//
// Shared by FootnoteRefNode and EndnoteRefNode. Renders the whitelisted body
// HTML (bold / italic / link) visually, accepts user edits, and serializes
// back through the same whitelist so pasted/Word marks survive editing.
//
// Toolbar: B / I / Link / Unlink. Buttons show active state when the caret
// sits inside the corresponding mark. Keyboard shortcuts mirror the toolbar
// (Cmd/Ctrl+B, +I, +K). Implemented with document.execCommand for the
// smallest surface area — TipTap-in-Popover would be overkill here.

import { useCallback, useEffect, useRef, useState } from "react";
import { Bold, Italic, Link2, Link2Off } from "lucide-react";
import { Toggle } from "@/components/ui/toggle";
import { Button } from "@/components/ui/button";
import {
  renderFootnoteBodyHtml,
  serializeBodyFromElement,
  serializeBodyFromHtml,
} from "@/lib/briefDocument/footnoteBody";

export type FootnoteBodyEditorProps = {
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  ariaLabel?: string;
};

const isInsideTag = (tagNames: string[]): boolean => {
  if (typeof window === "undefined") return false;
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return false;
  let node: Node | null = sel.getRangeAt(0).startContainer;
  while (node) {
    if (node.nodeType === 1) {
      const tag = (node as Element).tagName.toLowerCase();
      if (tagNames.includes(tag)) return true;
      if ((node as Element).getAttribute?.("contenteditable") === "true") return false;
    }
    node = node.parentNode;
  }
  return false;
};

export const FootnoteBodyEditor = ({
  value,
  onChange,
  placeholder = "Write the note. You can paste from Word or Docs — formatting and links are preserved.",
  ariaLabel = "Note body",
}: FootnoteBodyEditorProps) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [activeMarks, setActiveMarks] = useState({ bold: false, italic: false, link: false });
  const savedSelectionRef = useRef<Range | null>(null);

  // Seed the contenteditable on mount + when the external value changes from
  // somewhere other than this editor. We avoid resetting innerHTML on every
  // keystroke (would lose caret position).
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const current = serializeBodyFromElement(el);
    if (current !== value) {
      el.innerHTML = renderFootnoteBodyHtml(value);
    }
  }, [value]);

  const refreshMarks = useCallback(() => {
    setActiveMarks({
      bold: isInsideTag(["strong", "b"]),
      italic: isInsideTag(["em", "i"]),
      link: isInsideTag(["a"]),
    });
  }, []);

  const emit = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    onChange(serializeBodyFromElement(el));
    refreshMarks();
  }, [onChange, refreshMarks]);

  const exec = (cmd: "bold" | "italic") => {
    const el = ref.current;
    if (!el) return;
    el.focus();
    document.execCommand(cmd);
    emit();
  };

  const saveSelection = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) savedSelectionRef.current = sel.getRangeAt(0).cloneRange();
  };

  const restoreSelection = () => {
    const r = savedSelectionRef.current;
    if (!r) return;
    const sel = window.getSelection();
    if (sel) {
      sel.removeAllRanges();
      sel.addRange(r);
    }
  };

  const openLinkInput = () => {
    saveSelection();
    setShowLinkInput((v) => !v);
  };

  const applyLink = () => {
    const url = linkUrl.trim();
    setShowLinkInput(false);
    setLinkUrl("");
    if (!url) return;
    const el = ref.current;
    if (!el) return;
    el.focus();
    restoreSelection();
    // execCommand handles selection-collapse case by inserting the URL as text
    // wrapped in <a>. Good enough for v1.
    document.execCommand("createLink", false, url);
    emit();
  };

  const removeLink = () => {
    const el = ref.current;
    if (!el) return;
    el.focus();
    document.execCommand("unlink");
    emit();
  };

  // Keyboard shortcuts mirror the toolbar.
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const mod = e.metaKey || e.ctrlKey;
    if (!mod) return;
    const key = e.key.toLowerCase();
    if (key === "b") {
      e.preventDefault();
      exec("bold");
    } else if (key === "i") {
      e.preventDefault();
      exec("italic");
    } else if (key === "k") {
      e.preventDefault();
      openLinkInput();
    }
  };

  // Paste through whitelist so users can't smuggle arbitrary HTML in.
  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    const html = e.clipboardData.getData("text/html");
    const text = e.clipboardData.getData("text/plain");
    const cleaned = html ? serializeBodyFromHtml(html) : (text ?? "");
    if (cleaned) {
      document.execCommand("insertHTML", false, cleaned);
      emit();
    }
  };

  const isEmpty = !value || value.trim() === "";

  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-0.5 rounded-md border border-input bg-muted/30 p-0.5">
        <Toggle
          size="sm"
          pressed={activeMarks.bold}
          onMouseDown={(e) => e.preventDefault()}
          onPressedChange={() => exec("bold")}
          aria-label="Bold (⌘B)"
          title="Bold (⌘B)"
          className="h-7 w-7 p-0"
        >
          <Bold className="h-3.5 w-3.5" />
        </Toggle>
        <Toggle
          size="sm"
          pressed={activeMarks.italic}
          onMouseDown={(e) => e.preventDefault()}
          onPressedChange={() => exec("italic")}
          aria-label="Italic (⌘I)"
          title="Italic (⌘I)"
          className="h-7 w-7 p-0"
        >
          <Italic className="h-3.5 w-3.5" />
        </Toggle>
        <div className="mx-1 h-4 w-px bg-border" aria-hidden="true" />
        <Toggle
          size="sm"
          pressed={showLinkInput || activeMarks.link}
          onMouseDown={(e) => {
            e.preventDefault();
            saveSelection();
          }}
          onPressedChange={() => setShowLinkInput((v) => !v)}
          aria-label="Insert link (⌘K)"
          title="Insert link (⌘K)"
          className="h-7 w-7 p-0"
        >
          <Link2 className="h-3.5 w-3.5" />
        </Toggle>
        {activeMarks.link ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            aria-label="Remove link"
            title="Remove link"
            onMouseDown={(e) => e.preventDefault()}
            onClick={removeLink}
          >
            <Link2Off className="h-3.5 w-3.5" />
          </Button>
        ) : null}
      </div>

      {showLinkInput ? (
        <div className="flex items-center gap-1.5">
          <input
            type="url"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            placeholder="https://…"
            autoFocus
            className="h-7 flex-1 rounded border border-input bg-background px-2 text-xs outline-none focus:ring-1 focus:ring-ring"
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                applyLink();
              } else if (e.key === "Escape") {
                e.preventDefault();
                setShowLinkInput(false);
                setLinkUrl("");
              }
            }}
          />
          <Button type="button" size="sm" className="h-7 px-2 text-xs" onClick={applyLink}>
            Apply
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs"
            onClick={() => {
              setShowLinkInput(false);
              setLinkUrl("");
            }}
          >
            Cancel
          </Button>
        </div>
      ) : null}

      <div className="relative">
        <div
          ref={ref}
          contentEditable
          suppressContentEditableWarning
          role="textbox"
          aria-label={ariaLabel}
          aria-multiline="true"
          className="min-h-24 w-full rounded-md border border-input bg-background px-3 py-2 text-sm leading-6 outline-none focus:ring-1 focus:ring-ring [&_a]:text-primary [&_a]:underline"
          onInput={emit}
          onBlur={emit}
          onPaste={handlePaste}
          onKeyDown={handleKeyDown}
          onKeyUp={refreshMarks}
          onMouseUp={refreshMarks}
          onFocus={refreshMarks}
        />
        {isEmpty ? (
          <div className="pointer-events-none absolute inset-0 px-3 py-2 text-sm text-muted-foreground">
            {placeholder}
          </div>
        ) : null}
      </div>
      <p className="hidden md:block text-[11px] text-muted-foreground">
        Shortcuts: <kbd className="rounded border bg-muted px-1">⌘B</kbd>{" "}
        <kbd className="rounded border bg-muted px-1">⌘I</kbd>{" "}
        <kbd className="rounded border bg-muted px-1">⌘K</kbd>
      </p>
    </div>
  );
};
